import * as XLSX from 'xlsx';
import {
  finalizeHeatingTableDisplayRows,
  HeatingTableDisplayRow,
  isTotalRow,
  normalizeSteamTableRows,
  SteamTableRowConfig,
} from './steam-table-extract.util';

export interface HeatingTableColumnDef {
  key: string;
  label: string;
}

export interface DynamicHeatingTableResult {
  columns: HeatingTableColumnDef[];
  rows: Record<string, string>[];
  source?: string;
}

function cellToString(value: unknown): string {
  if (value == null) {
    return '';
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return String(value).trim();
}

function isTotalRowLabel(label: string): boolean {
  return /^total$/i.test(label.trim());
}

export function columnKeyFromLabel(
  label: string,
  index: number,
  used: Set<string>,
): string {
  const base =
    label
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || `col_${index}`;
  let key = base;
  let suffix = 1;
  while (used.has(key)) {
    key = `${base}_${suffix++}`;
  }
  used.add(key);
  return key;
}

function splitDelimitedLine(line: string): string[] {
  if (line.includes('\t')) {
    return line.split('\t').map((part) => part.trim());
  }
  if (line.includes('|')) {
    return line.split('|').map((part) => part.trim());
  }
  if (line.includes(',')) {
    return line
      .split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/)
      .map((part) => part.replace(/^"|"$/g, '').trim());
  }
  if (line.includes(';')) {
    return line.split(';').map((part) => part.trim());
  }
  if (/\s{2,}/.test(line)) {
    return line
      .split(/\s{2,}/)
      .map((part) => part.trim())
      .filter(Boolean);
  }
  return [line.trim()];
}

function scoreHeaderRow(parts: string[]): number {
  if (parts.length < 1) {
    return 0;
  }

  let score = parts.filter((part) => part.length > 0).length * 2;
  const joined = parts.join(' ').toLowerCase();
  if (/tank|capacity|steam|heating|temperature|name|column|header/i.test(joined)) {
    score += 8;
  }

  const numericCount = parts.filter((part) =>
    /^\d+([.,]\d+)?$/.test(part.replace(/,/g, '')),
  ).length;
  if (numericCount > parts.length / 2) {
    score -= 10;
  }

  return score;
}

function detectHeaderRowIndex(matrix: string[][]): number {
  let bestIdx = 0;
  let bestScore = 0;
  const limit = Math.min(matrix.length, 10);

  for (let i = 0; i < limit; i++) {
    const score = scoreHeaderRow(matrix[i]);
    if (score > bestScore) {
      bestScore = score;
      bestIdx = i;
    }
  }

  return bestIdx;
}

function padRow(row: string[], width: number): string[] {
  const padded = [...row];
  while (padded.length < width) {
    padded.push('');
  }
  return padded.slice(0, width);
}

export function buildDynamicTableFromMatrix(
  matrix: string[][],
): DynamicHeatingTableResult | null {
  const cleaned = matrix
    .map((row) => row.map((cell) => cellToString(cell)))
    .filter((row) => row.some((cell) => cell.length > 0));

  if (cleaned.length < 1) {
    return null;
  }

  const maxCols = Math.max(...cleaned.map((row) => row.length));
  if (maxCols < 1) {
    return null;
  }

  const normalized = cleaned.map((row) => padRow(row, maxCols));
  const headerIdx = detectHeaderRowIndex(normalized);
  const headerRow = normalized[headerIdx];
  const usedKeys = new Set<string>();
  const columns: HeatingTableColumnDef[] = [];

  for (let i = 0; i < maxCols; i++) {
    const label = (headerRow[i] ?? '').trim() || `Column ${i + 1}`;
    columns.push({
      key: columnKeyFromLabel(label, i, usedKeys),
      label,
    });
  }

  const rows: Record<string, string>[] = [];
  for (let rowIdx = headerIdx + 1; rowIdx < normalized.length; rowIdx++) {
    const cells = normalized[rowIdx];
    const firstCell = (cells[0] ?? '').trim();
    if (isTotalRowLabel(firstCell)) {
      continue;
    }

    const row: Record<string, string> = {};
    let hasValue = false;
    for (let colIdx = 0; colIdx < columns.length; colIdx++) {
      const value = cellToString(cells[colIdx] ?? '');
      row[columns[colIdx].key] = value;
      if (value) {
        hasValue = true;
      }
    }

    if (hasValue) {
      rows.push(row);
    }
  }

  if (!rows.length && normalized.length === 1) {
    return null;
  }

  if (!rows.length) {
    return null;
  }

  return { columns, rows };
}

export function parseDynamicTableFromText(
  content: string,
): DynamicHeatingTableResult | null {
  const lines = content
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (!lines.length) {
    return null;
  }

  const matrix = lines.map(splitDelimitedLine);
  return buildDynamicTableFromMatrix(matrix);
}

export async function parseDynamicTableFromSpreadsheet(
  file: File,
): Promise<DynamicHeatingTableResult | null> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const matrix: string[][] = [];

  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) {
      continue;
    }

    const rows = XLSX.utils.sheet_to_json<(string | number | null)[]>(sheet, {
      header: 1,
      defval: '',
      raw: false,
    });

    for (const row of rows) {
      if (!Array.isArray(row)) {
        continue;
      }
      const cells = row.map((cell) => cellToString(cell));
      if (cells.some((cell) => cell.length > 0)) {
        matrix.push(cells);
      }
    }
  }

  return buildDynamicTableFromMatrix(matrix);
}

export function normalizeDynamicTableJson(
  parsed: unknown,
): DynamicHeatingTableResult | null {
  if (parsed == null) {
    return null;
  }

  if (Array.isArray(parsed)) {
    if (!parsed.length || typeof parsed[0] !== 'object' || parsed[0] === null) {
      return null;
    }

    const labels = Object.keys(parsed[0] as object);
    if (!labels.length) {
      return null;
    }

    const usedKeys = new Set<string>();
    const columns = labels.map((label, index) => ({
      key: columnKeyFromLabel(label, index, usedKeys),
      label,
    }));

    const rows = (parsed as Record<string, unknown>[])
      .map((item) => {
        const row: Record<string, string> = {};
        for (const col of columns) {
          row[col.key] = cellToString(item[col.label]);
        }
        return row;
      })
      .filter((row) => Object.values(row).some((value) => value.length > 0));

    return rows.length ? { columns, rows } : null;
  }

  if (typeof parsed !== 'object') {
    return null;
  }

  const obj = parsed as Record<string, unknown>;
  const explicitColumns = obj['columns'] ?? obj['headers'];
  const explicitRows = obj['rows'] ?? obj['data'];

  if (!Array.isArray(explicitColumns) || !Array.isArray(explicitRows)) {
    return null;
  }

  const labels = explicitColumns
    .map((column) => cellToString(column))
    .filter((label) => label.length > 0);
  if (!labels.length) {
    return null;
  }

  const usedKeys = new Set<string>();
  const columns = labels.map((label, index) => ({
    key: columnKeyFromLabel(label, index, usedKeys),
    label,
  }));

  const rows: Record<string, string>[] = [];
  for (const rawRow of explicitRows) {
    if (Array.isArray(rawRow)) {
      const row: Record<string, string> = {};
      columns.forEach((col, index) => {
        row[col.key] = cellToString(rawRow[index]);
      });
      if (Object.values(row).some((value) => value.length > 0)) {
        rows.push(row);
      }
      continue;
    }

    if (rawRow && typeof rawRow === 'object') {
      const record = rawRow as Record<string, unknown>;
      const row: Record<string, string> = {};
      for (const col of columns) {
        row[col.key] = cellToString(
          record[col.label] ??
            record[col.key] ??
            record[col.label.toLowerCase()] ??
            '',
        );
      }
      if (Object.values(row).some((value) => value.length > 0)) {
        rows.push(row);
      }
    }
  }

  return rows.length ? { columns, rows } : null;
}

export function parseGeminiDynamicHeatingTableResponse(
  text: string,
): DynamicHeatingTableResult | null {
  try {
    const cleaned = text
      .trim()
      .replace(/^```json\s*/i, '')
      .replace(/```$/i, '');
    const parsed = JSON.parse(cleaned);
    const direct = normalizeDynamicTableJson(parsed);
    if (direct) {
      return direct;
    }
    if (Array.isArray((parsed as { rows?: unknown[] })?.rows)) {
      return normalizeDynamicTableJson((parsed as { rows: unknown[] }).rows);
    }
    return null;
  } catch {
    return null;
  }
}

export function dynamicTableFromSteamRows(
  rows: SteamTableRowConfig[],
): DynamicHeatingTableResult {
  const columns: HeatingTableColumnDef[] = [
    { key: 'tank_name', label: 'Tank Name' },
    { key: 'capacity', label: 'Tank Capacity' },
    { key: 'heating_surface_m2', label: 'Heating Surface (m²)' },
    { key: 'max_steam', label: 'Max Steam (kg/hr)' },
    { key: 'step_requirement', label: 'Step Requirement' },
  ];

  return {
    columns,
    rows: rows.map((row) => ({
      tank_name: row.tankName,
      capacity: cellToString(row.capacity),
      heating_surface_m2:
        row.heatingSurfaceM2 != null ? cellToString(row.heatingSurfaceM2) : '',
      max_steam: row.maxSteam != null ? cellToString(row.maxSteam) : '',
      step_requirement:
        row.stepRequirement != null ? cellToString(row.stepRequirement) : '',
    })),
  };
}

export function dynamicTableFromLegacyTankRows(
  rows: Array<Record<string, unknown>>,
  columns?: HeatingTableColumnDef[],
): DynamicHeatingTableResult | null {
  if (!rows.length) {
    return null;
  }

  const tableColumns =
    columns?.length ? columns : inferColumnsFromLegacyRows(rows);
  if (!tableColumns.length) {
    return null;
  }

  const mappedRows = rows.map((row) => {
    const mapped: Record<string, string> = {};
    for (const col of tableColumns) {
      mapped[col.key] = cellToString(
        row[col.key] ?? row[col.label] ?? resolveLegacyCellValue(row, col.label),
      );
    }
    return mapped;
  });

  return { columns: tableColumns, rows: mappedRows };
}

function inferColumnsFromLegacyRows(
  rows: Array<Record<string, unknown>>,
): HeatingTableColumnDef[] {
  const labelSet = new Set<string>();
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (row[key] != null && row[key] !== '') {
        labelSet.add(legacyKeyToLabel(key));
      }
    }
  }

  const labels = Array.from(labelSet);
  const usedKeys = new Set<string>();
  return labels.map((label, index) => ({
    key: columnKeyFromLabel(label, index, usedKeys),
    label,
  }));
}

function legacyKeyToLabel(key: string): string {
  const map: Record<string, string> = {
    name: 'Tank Name',
    tankName: 'Tank Name',
    capacity: 'Tank Capacity',
    tankCapacityM3: 'Tank Capacity',
    heatingSurfaceM2: 'Heating Surface (m²)',
    maxSteam: 'Max Steam (kg/hr)',
    maxSteamKgHr: 'Max Steam (kg/hr)',
    stepRequirement: 'Step Requirement',
  };
  return map[key] ?? key;
}

function resolveLegacyCellValue(
  row: Record<string, unknown>,
  label: string,
): unknown {
  const normalizedLabel = label.toLowerCase();
  if (/tank.*name|^name$/i.test(normalizedLabel)) {
    return row['name'] ?? row['tankName'] ?? '';
  }
  if (/capacity/i.test(normalizedLabel)) {
    return row['capacity'] ?? row['tankCapacityM3'] ?? row['capacityM3'] ?? '';
  }
  if (/heating.*surface/i.test(normalizedLabel)) {
    return row['heatingSurfaceM2'] ?? row['heatingSurface'] ?? '';
  }
  if (/max.*steam|steam.*consumption/i.test(normalizedLabel)) {
    return row['maxSteam'] ?? row['maxSteamKgHr'] ?? row['maxSteamMt'] ?? '';
  }
  if (/step|requirement|condition/i.test(normalizedLabel)) {
    return row['stepRequirement'] ?? '';
  }

  return row[label] ?? '';
}

export type DynamicHeatingTableSource =
  | 'vision'
  | 'spreadsheet'
  | 'json'
  | 'profile'
  | 'legacy'
  | 'text';

export interface TaggedDynamicHeatingCandidate {
  result: DynamicHeatingTableResult;
  source: DynamicHeatingTableSource;
}

const SOURCE_PRIORITY: Record<DynamicHeatingTableSource, number> = {
  vision: 1000,
  spreadsheet: 800,
  json: 700,
  profile: 600,
  legacy: 500,
  text: 200,
};

const OCR_GARBAGE_TERMS =
  /\b(swan|scores|corcor|comyeent|susiigl|hetnu|sewrun|won|oom|vom)\b/i;

const MARINE_TABLE_TERMS = [
  'tank',
  'capacity',
  'heating',
  'steam',
  'surface',
  'ratio',
  'cot',
  'slop',
  'pipe',
  'branch',
  'column',
  'grid',
  'm3',
  'm²',
  'm2',
  'no.',
  'port',
  'soneok',
];

/** Score plain OCR / PDF text before attempting table parsing. */
export function scoreExtractionTextQuality(text: string): number {
  const trimmed = text.trim();
  if (trimmed.length < 20) {
    return 0;
  }

  const compact = trimmed.replace(/\s/g, '');
  const letters = (compact.match(/[a-zA-Z]/g) || []).length;
  const digits = (compact.match(/\d/g) || []).length;
  const brackets = (compact.match(/[\[\](){}]/g) || []).length;
  const lower = trimmed.toLowerCase();
  const termHits = MARINE_TABLE_TERMS.filter((term) => lower.includes(term)).length;
  const garbageHits = (lower.match(OCR_GARBAGE_TERMS) || []).length;

  let score =
    termHits * 12 + digits * 0.6 + letters * 0.25 - brackets * 2 - garbageHits * 30;

  const words = trimmed.split(/\s+/).filter((word) => word.length > 1);
  const avgWordLen =
    words.reduce((sum, word) => sum + word.length, 0) / Math.max(words.length, 1);
  if (avgWordLen < 3.2) {
    score -= 25;
  }

  return score;
}

export function isReliableExtractionText(text: string): boolean {
  return scoreExtractionTextQuality(text) >= 35;
}

export function isUsableDynamicHeatingTable(
  result: DynamicHeatingTableResult | null | undefined,
): boolean {
  return !!result?.columns.length && !!result.rows.length;
}

/** Reject low-confidence OCR tables (garbled headers/cells). */
export function isReliableDynamicHeatingTable(
  result: DynamicHeatingTableResult | null | undefined,
): boolean {
  if (!isUsableDynamicHeatingTable(result)) {
    return false;
  }

  const table = result!;
  const allText = [
    ...table.columns.map((col) => col.label),
    ...table.rows.flatMap((row) => Object.values(row)),
  ].join(' ');

  if (!isReliableExtractionText(allText)) {
    return false;
  }

  const garbageRows = table.rows.filter((row) =>
    OCR_GARBAGE_TERMS.test(Object.values(row).join(' ')),
  ).length;
  if (garbageRows / table.rows.length > 0.15) {
    return false;
  }

  const hasTankRow = table.rows.some((row) => {
    const joined = Object.values(row).join(' ');
    return (
      /(?:NO\.?\s*\d+|SLOP).*(?:\(P\)|\(S\))/i.test(joined) ||
      /\d+\s*PORT\s*\(/i.test(joined) ||
      /C\.?\s*O\.?\s*T(?:K)?/i.test(joined)
    );
  });

  let numericCells = 0;
  let filledCells = 0;
  for (const row of table.rows) {
    for (const value of Object.values(row)) {
      const trimmed = value.trim();
      if (!trimmed) {
        continue;
      }
      filledCells++;
      if (/^[\d,.\-]+$/.test(trimmed)) {
        numericCells++;
      }
    }
  }

  const numericRatio = filledCells > 0 ? numericCells / filledCells : 0;
  const hasDataRow = table.rows.some((row) => {
    const values = Object.values(row)
      .map((value) => value.trim())
      .filter(Boolean);
    const numCount = values.filter((value) => /^[\d,.\-]+$/.test(value)).length;
    return numCount >= 3 || /(?:NO\.?\s*\d+|SLOP)/i.test(values.join(' '));
  });

  if (hasTankRow && hasDataRow) {
    return true;
  }
  if (numericRatio >= 0.3 && hasDataRow) {
    return true;
  }
  if (table.columns.length === 2 && table.rows.length >= 1 && numericRatio >= 0.2) {
    return true;
  }

  return false;
}

export function scoreDynamicHeatingTable(
  result: DynamicHeatingTableResult,
): number {
  let score = result.rows.length * 4 + result.columns.length * 3;
  const labels = result.columns.map((col) => col.label.toLowerCase()).join(' ');
  if (/tank|capacity|steam|heating|temperature/i.test(labels)) {
    score += 12;
  }

  const filledCells = result.rows.reduce(
    (sum, row) => sum + Object.values(row).filter((value) => value.trim()).length,
    0,
  );
  score += filledCells;
  score += scoreExtractionTextQuality(
    [
      ...result.columns.map((col) => col.label),
      ...result.rows.flatMap((row) => Object.values(row)),
    ].join(' '),
  );

  return score;
}

function normalizeCandidate(
  candidate:
    | DynamicHeatingTableResult
    | TaggedDynamicHeatingCandidate
    | null
    | undefined,
): TaggedDynamicHeatingCandidate | null {
  if (!candidate) {
    return null;
  }
  if ('result' in candidate && candidate.result) {
    return candidate as TaggedDynamicHeatingCandidate;
  }
  return {
    result: candidate as DynamicHeatingTableResult,
    source: 'text',
  };
}

export function pickBestDynamicHeatingTable(
  candidates: Array<
    | DynamicHeatingTableResult
    | TaggedDynamicHeatingCandidate
    | null
    | undefined
  >,
  options?: { requireReliable?: boolean },
): DynamicHeatingTableResult | null {
  const requireReliable = options?.requireReliable ?? true;
  let best: DynamicHeatingTableResult | null = null;
  let bestScore = 0;

  for (const raw of candidates) {
    const tagged = normalizeCandidate(raw);
    if (!tagged || !isUsableDynamicHeatingTable(tagged.result)) {
      continue;
    }
    if (requireReliable && !isReliableDynamicHeatingTable(tagged.result)) {
      continue;
    }

    const score =
      scoreDynamicHeatingTable(tagged.result) +
      (SOURCE_PRIORITY[tagged.source] ?? 0);
    if (score > bestScore) {
      bestScore = score;
      best = tagged.result;
    }
  }

  return best;
}

function parseNumber(raw: string): number | null {
  const cleaned = raw.replace(/,/g, '').replace(/[^\d.-]/g, '');
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

export function mapDynamicRowToLegacyTank(
  row: Record<string, string>,
  columns: HeatingTableColumnDef[],
): {
  name: string;
  capacity: number | null;
  heatingSurfaceM2: number | null;
  maxSteam: number | null;
  stepRequirement: string;
} {
  const findByLabel = (patterns: RegExp[]): string => {
    for (const col of columns) {
      const label = col.label.toLowerCase();
      if (patterns.some((pattern) => pattern.test(label))) {
        return row[col.key] ?? '';
      }
    }
    return '';
  };

  const name =
    findByLabel([/tank.*name/, /^name$/, /compartment/, /tank\s*no/]) ||
    row[columns[0]?.key] ||
    '';
  const capacity = parseNumber(findByLabel([/capacity/, /m3/, /m³/, /volume/]));
  const heatingSurfaceM2 = parseNumber(
    findByLabel([/heating.*surface/, /surface.*m/, /grid.*pipe/]),
  );
  const maxSteam = parseNumber(
    findByLabel([/max.*steam/, /steam.*consumption/, /kg.*hr/]),
  );
  const stepRequirement = findByLabel([/step/, /requirement/, /condition/]);

  return {
    name: name.trim(),
    capacity,
    heatingSurfaceM2,
    maxSteam,
    stepRequirement: stepRequirement.trim(),
  };
}

export function steamRowsToDynamicCandidates(
  rawRows: unknown[],
): DynamicHeatingTableResult | null {
  const normalized = normalizeSteamTableRows(rawRows as any[]);
  if (!normalized.length) {
    return null;
  }
  return dynamicTableFromSteamRows(normalized);
}

export function mapDynamicResultToDisplayRows(
  result: DynamicHeatingTableResult,
): HeatingTableDisplayRow[] {
  const rows: HeatingTableDisplayRow[] = [];

  for (const row of result.rows) {
    const mapped = mapDynamicRowToLegacyTank(row, result.columns);
    const fallbackName = row[result.columns[0]?.key] ?? '';
    const tankName = (mapped.name || fallbackName).trim();
    if (!tankName) {
      continue;
    }

    const isTotal = isTotalRow(tankName);
    if (!isTotal && (!mapped.capacity || mapped.capacity <= 0)) {
      continue;
    }

    rows.push({
      tankName: isTotal ? 'TOTAL' : tankName,
      capacity: mapped.capacity ?? 0,
      heatingSurfaceM2: mapped.heatingSurfaceM2,
      maxSteam: mapped.maxSteam,
      isTotal,
    });
  }

  return finalizeHeatingTableDisplayRows(rows);
}

export function mapSpreadsheetToDisplayRows(
  result: DynamicHeatingTableResult,
): HeatingTableDisplayRow[] {
  return mapDynamicResultToDisplayRows(result);
}
export const DEMO_VESSEL_IMOS = ['9312133', '1044223'];
export const DEMO_VESSEL_PC =['9399480','9438406']